import { createRouter, Navigate, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HawkErrorBoundary } from "@/lib/error-boundary";
import { initHawk, reportError } from "@/lib/hawk";
import { localeReady } from "@/lib/i18n";
import { AppProviders } from "@/providers/app-providers";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

void initHawk("sso-web");

const router = createRouter({
	routeTree,
	// Неизвестный адрес — на экран объяснения, а не на «Not Found» роутера.
	// Домен входа обязан оставаться дверью: сюда приходят по старым ссылкам из
	// писем, по закладкам и по опечаткам, и упереться в служебную страницу
	// здесь хуже, чем где бы то ни было ещё.
	defaultNotFoundComponent: () => <Navigate to="/error" replace />,
	defaultPreload: "intent",
	defaultPreloadStaleTime: 0,
	scrollRestoration: true,
	defaultOnCatch: (error, errorInfo) => {
		reportError(error instanceof Error ? error : String(error), {
			source: "router",
			componentStack: errorInfo.componentStack,
		});
	},
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

/** Сколько ждать словарь, прежде чем рисовать на английском. */
const LOCALE_WAIT_MS = 1500;

/**
 * Первый рендер — уже на нужном языке.
 *
 * Словарь локали приезжает отдельным чанком (21 локаль, в основном бандле
 * только английский), поэтому без ожидания человек видел английский интерфейс,
 * который через мгновение переписывался: менялся текст, а с ним и высота
 * блоков. Чанк грузится параллельно с бандлом, так что ожидание почти всегда
 * нулевое, а `Promise.race` страхует от медленной сети — лучше английский
 * интерфейс, чем экран, который не появился.
 */
async function mount(): Promise<void> {
	await Promise.race([localeReady, new Promise((resolve) => setTimeout(resolve, LOCALE_WAIT_MS))]);

	// Элемент ищем здесь, а не на верхнем уровне: скелет из `index.html` лежит
	// внутри него, и React очистит контейнер ровно в момент первого рендера.
	const rootElement = document.getElementById("root");
	if (!rootElement) throw new Error("Не найден #root — проверь index.html");

	createRoot(rootElement).render(
		<StrictMode>
			<HawkErrorBoundary>
				<AppProviders>
					<RouterProvider router={router} />
				</AppProviders>
			</HawkErrorBoundary>
		</StrictMode>,
	);
}

void mount();
